import React, { useEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import {
    Box,
    Stack,
    Typography,
    Card,
    CardContent,
    CardActions,
    Button,
    Avatar,
    Grid,
} from '@mui/material';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import ContentCardsContainer, { ContentCard } from 'src/components/organisms/ContentCard';

// If you changed your import paths, keep these lines matching your app:
import menuItems from 'src/components/organisms/sidebarMenu/items';
import { pagesRoutes } from 'src/routes';

// ---------------------------------------------
// Route helpers
// ---------------------------------------------

type RouteNode = {
    path?: string;
    element?: React.ReactNode;
    children?: RouteNode[];
};

type RouteIndex = {
    pages: Set<string>;
    createByBase: Record<string, string>;
};

const normalize = (s: string) => {
    let out = ('/' + s).replace(/\/+/, '/');
    out = out.replace(/\/+$/, '');
    return out === '' ? '/' : out;
};

const join = (a: string, b: string) => normalize(`${normalize(a)}/${b}`);

function buildRouteIndex(routes: RouteNode[]): RouteIndex {
    const pages = new Set<string>();
    const createByBase: Record<string, string> = {};

    const walk = (nodes: RouteNode[], base: string) => {
        nodes.forEach((n) => {
            const here = n.path ? join(base, n.path) : base;
            if (n.element) pages.add(here);
            if (n.children?.length) walk(n.children, here);

            // map ".../create" back to its base
            if (/\/create\/?$/i.test(here)) {
                const basePath = here.replace(/\/create\/?$/i, '');
                createByBase[basePath] = here;
            }
        });
    };

    walk(routes as RouteNode[], '');
    return { pages, createByBase };
}

// ---------------------------------------------
// Build visible groups from the menu (menus define semantics)
// ---------------------------------------------

type MenuIcon = React.ElementType;

type MenuLeaf = {
    title: string;
    link: string;
    Icon?: MenuIcon;
    subgroup?: string;
};

type MenuGroup = {
    id: string;       // first segment, e.g. "orders"
    title: string;    // human label, e.g. "Orders"
    leaves: MenuLeaf[];
};

interface MenuItemNode {
    name: string;
    link?: string;
    icon?: MenuIcon;
    items?: MenuItemNode[];
}

const firstSegment = (path: string) => normalize(path).split('/')[1] || '';

const titleCase = (input: string) =>
    input
      .toLowerCase()
      .replace(/(^|[\s\-_\/])(\w)/g, (_, d, c) => (d ? d : '') + c.toUpperCase())
      .replace(/[-_/]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

function collectLeaves(node: MenuItemNode, trail: string[] = []): MenuLeaf[] {
    if (node.items?.length) {
        const subgroupName = trail.length ? trail[trail.length - 1] : undefined;
        const leaves: MenuLeaf[] = [];
        node.items.forEach((child) => {
            if (child.items?.length) {
                leaves.push(...collectLeaves(child, [...trail, child.name]));
            } else if (child.link) {
                leaves.push({
                    title: child.name,
                    link: normalize(child.link),
                    Icon: child.icon,
                    subgroup: subgroupName
                });
            }
        });
        return leaves;
    }
    if (node.link) return [{ title: node.name, link: normalize(node.link), Icon: node.icon }];
    return [];
}

function buildGroupsFromMenu(routeIdx: RouteIndex): MenuGroup[] {
    const groups: MenuGroup[] = [];

    (menuItems as any[]).forEach((section: any) => {
        (section.items as MenuItemNode[]).forEach((root) => {
            if (!root.items?.length) return;

            const leaves = collectLeaves(root).filter((leaf) => routeIdx.pages.has(leaf.link));
            if (!leaves.length) return;

            const id = firstSegment(leaves[0].link);
            groups.push({ id, title: titleCase(root.name), leaves });
        });
    });

    // merge by base segment (if needed)
    const byId: Record<string, MenuGroup> = {};
    groups.forEach((g) => {
        if (!byId[g.id]) byId[g.id] = { ...g, leaves: [...g.leaves] };
        else byId[g.id].leaves.push(...g.leaves);
    });

    return Object.values(byId);
}

// ---------------------------------------------
// UI — page card
// ---------------------------------------------

function PageCard({
    title,
    link,
    Icon,
    createHref
}: {
    title: string;
    link: string;
    Icon?: MenuIcon;
    createHref?: string;
}) {
    const pretty = titleCase(title);
    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardContent sx={{ flexGrow: 1, pb:{sm:2,md:10} }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar variant="rounded" sx={{ width: 56, height: 56, fontSize: 28 }}>
                        {Icon ? React.createElement(Icon, { fontSize: 'inherit' as any }) : pretty[0]}
                    </Avatar>
                    <Typography variant="h6" fontWeight={800}>
                        {pretty}
                    </Typography>
                </Stack>
            </CardContent>


        
            <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
                <Button component={RouterLink} to={link} variant="contained" size="small">
                    View All
                </Button>
                {createHref && (
                    <Button
                        component={RouterLink}
                        to={createHref}
                        startIcon={<AddTwoToneIcon />}
                        variant="outlined"
                        size="small"
                    >
                        New {pretty}
                    </Button>
                )}
            </CardActions>
        </Card>
    );
}

// ---------------------------------------------
// Group card — single flat grid (no subsection headers)
// ---------------------------------------------

function GroupCard({
    group,
    routeIdx
}: {
    group: MenuGroup;
    routeIdx: RouteIndex;
}) {
    const leaves = group.leaves;

    return (
        <ContentCard cardId={titleCase(group.id)}>
            <Box id={group.id} sx={{ position: 'relative', top: -88 }} />

            {/* NEW: make the grid a full-width Grid item so it hugs the left */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(3, minmax(0, 1fr))',
                            lg: 'repeat(3, minmax(0, 1fr))',
                        },
                        justifyContent: 'flex-start',
                        justifyItems: 'stretch',
                        alignItems: 'stretch'
                    }}
                >
                    {leaves.map((leaf) => (
                        <PageCard
                            key={leaf.link}
                            
                            title={leaf.title}
                            link={leaf.link}
                            Icon={leaf.Icon}
                            createHref={routeIdx.createByBase[leaf.link]}
                        />
                    ))}
                </Box>
            </Grid>
        </ContentCard>
    );
}


// ---------------------------------------------
// Scroll helper (paint-safe + header offset)
// ---------------------------------------------

function scrollToSection(id: string, offset = 88) {
    const el = document.getElementById(id);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const top = rect.top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
}

// ---------------------------------------------
// Main component
// ---------------------------------------------

export default function OverviewPage() {
    const location = useLocation();

    const routeIdx = useMemo(() => buildRouteIndex(pagesRoutes as any), []);
    const groups = useMemo(() => buildGroupsFromMenu(routeIdx), [routeIdx]);

    // Auto-scroll to the correct section when visiting /products, /orders, etc.
    useEffect(() => {
        const seg = firstSegment(location.pathname);
        if (!seg) return;

        const raf1 = requestAnimationFrame(() => {
            const raf2 = requestAnimationFrame(() => scrollToSection(seg));
            return () => cancelAnimationFrame(raf2);
        });
        return () => cancelAnimationFrame(raf1);
    }, [location.pathname]);

    return (
        <>
            <PageHeader title="Overview" subTitle="An overview of all pages" />
            <ContentCardsContainer>
                {groups.map((group) => (
                    <GroupCard key={group.id} group={group} routeIdx={routeIdx} />
                ))}
            </ContentCardsContainer>
            <Footer />
        </>
    );
}
